// Official OFO-2021 quick-pick source for the BluePages registration skills step.
//
// The user-facing "trades & skills" list MUST come from the official OFO
// classification, not a hand-maintained array. This module loads the bundled
// OFO occupation dataset (public/ofo/ofo-codes.json — the FULL classification,
// all unit groups, not a trade-only subset) and exposes:
//   - QUICK_PICK_SKILLS: curated quick-pick pills (common trades + common
//     non-trade skills such as Software Developer), resolved to exact OFO
//     descriptions at load time so the pills and search share one source of truth
//   - loadOccupations: the complete, unfiltered occupation list
//
// Search over the full list (incl. semantic ranking for phrases like
// "web developer") lives in src/lib/ofo-client-search.ts (searchOfoCodes) —
// the registration wizard uses that, so queries resolve even when the
// occupation sits outside the trade major groups.

export interface OfoOccupation {
  code: string;
  description: string;
  majorGroup: string;
}

let cache: OfoOccupation[] | null = null;
let inflight: Promise<OfoOccupation[]> | null = null;

/** Load the FULL OFO occupation list (no major-group filtering). */
export async function loadOccupations(): Promise<OfoOccupation[]> {
  if (cache) return cache;
  if (inflight) return inflight;
  inflight = fetch(`${import.meta.env.BASE_URL}ofo/ofo-codes.json`)
    .then((r) => {
      if (!r.ok) throw new Error(`Failed to load OFO occupations: ${r.status}`);
      return r.json() as Promise<Array<Record<string, unknown>>>;
    })
    .then((rows) => {
      const all = rows
        .filter((r) => typeof r.code === 'string' && typeof r.description === 'string')
        .map((r) => ({
          code: r.code as string,
          description: r.description as string,
          majorGroup: (r.majorGroup as string) ?? '',
        }))
        .sort((a, b) => a.description.localeCompare(b.description));
      cache = all;
      QUICK_PICK_SKILLS = resolveCurated(all);
      inflight = null;
      return all;
    });
  return inflight;
}

/** Curated quick-pick pills surfaced in the registration wizard.
 *  Each entry is resolved to the EXACT official OFO description at load time so
 *  the pills and the autocomplete search share one canonical source of truth.
 *  Mix of common trades (major groups 6/7/8 + trade technicians) and common
 *  non-trade skills/professions so jobs like "web developer" have pills too. */
const CURATED_CHIPS = [
  // Trades
  'Electrician',
  'Plumber',
  'Carpenter and Joiner',
  'Bricklayer',
  'Painter',
  'Welder',
  'Wall and Floor Tiler',
  'Roof Tiler',
  'Plasterer',
  'Glazier',
  'Air-conditioning and Refrigeration Mechanic',
  'Solar Installer',
  'Automotive Motor Mechanic',
  'Diesel Mechanic',
  'Panelbeater',
  'Boilermaker',
  'Fitter and Turner',
  'Sheet Metal Worker',
  'Cabinetmaker',
  'Upholsterer',
  'Floor Finisher',
  'Scaffolder',
  'Rigger',
  'Locksmith',
  'Gardener',
  'Landscape Contractor',
  'House Builder',
  'Concreter',
  'Paving and Surfacing Worker',
  'Painter and Decorator',
  // Non-trade skills / professions
  'Software Developer',
  'Web Developer',
  'Data Entry Operator',
  'Bookkeeper',
  'General Clerk',
  'Receptionist (General)',
  'Hairdresser',
  'Cook',
  'Waiter',
  'Photographer',
  'Translator',
  'Programme or Project Manager',
];

function resolveCurated(all: OfoOccupation[]): string[] {
  const byDesc = new Map(all.map((t) => [t.description.toLowerCase(), t.description]));
  const out: string[] = [];
  for (const hint of CURATED_CHIPS) {
    const lower = hint.toLowerCase();
    if (byDesc.has(lower)) {
      out.push(byDesc.get(lower)!);
      continue;
    }
    const partial = all.find((t) => t.description.toLowerCase().includes(lower));
    if (partial) out.push(partial.description);
  }
  return out;
}

/** Curated quick-pick pill labels (resolved to exact OFO descriptions after
 *  loadOccupations resolves; initially the raw hints). */
export let QUICK_PICK_SKILLS: string[] = CURATED_CHIPS;
